import { Autocomplete, Skeleton, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import MuiModal, {
	MuiModalProps,
	OnCloseType,
} from '../../../../../mui/MuiModal/MuiModal';
import { Button } from '../../../../Button';
import { gql, DocumentNode } from '@apollo/client';
import { useToast } from '../../../../../hooks/useToast';
import styles from './InvoiceEditApplyTemplateModal.module.scss';
import {
	useInvoiceEditApplyTemplateModal_GetInvoiceTemplatesWhereQuery,
	SortOrderType,
} from '#types';
import {
	invoiceEditApplyTemplateModalInvoiceTemplateFragment,
	InvoiceTemplate,
} from '../../../types';

const invoiceEditApplyTemplateModalInvoiceFragment = gql`
	fragment invoiceEditApplyTemplateModalInvoice on Invoice {
		id
		invoiceTemplate {
			...invoiceEditApplyTemplateModalInvoiceTemplate
		}
	}
	${ invoiceEditApplyTemplateModalInvoiceTemplateFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const InvoiceEditApplyTemplateModal_getInvoiceTemplatesWhere = gql`
	query InvoiceEditApplyTemplateModal_getInvoiceTemplatesWhere(
		$orderBy: [InvoiceTemplateOrderByInput]
	) {
		getInvoiceTemplatesWhere(orderBy: $orderBy) {
			invoiceTemplates {
				...invoiceEditApplyTemplateModalInvoiceTemplate
			}
		}
	}
	${ invoiceEditApplyTemplateModalInvoiceTemplateFragment }
`;

export type InvoiceEditApplyTemplateModalProps = {
	invoiceTemplate?: InvoiceTemplate | null;
	setInvoiceTemplate: ( invoiceTemplate?: InvoiceTemplate | null ) => void;
} & Pick<MuiModalProps['muiModalProps'], 'open' | 'onClose'>;

type InvoiceEditApplyTemplateModalPropsWithFragment =
	React.FC<InvoiceEditApplyTemplateModalProps> & {
		fragments: {
			invoiceEditApplyTemplateModalInvoiceFragment: DocumentNode;
		};
	};

export const InvoiceEditApplyTemplateModal: InvoiceEditApplyTemplateModalPropsWithFragment =
	( { open, onClose, invoiceTemplate, setInvoiceTemplate } ) => {
		const { showError } = useToast();
		const [ title, setTitle ] = useState( 'Apply a template' );
		const [ description, setDescription ] = useState<
			string | React.ReactNode | undefined
		>();
		const [ buttonText, setButtonText ] = useState( 'Apply' );
		const [ selectedTemplate, setSelectedTemplate ] = useState<
			InvoiceTemplate | null | undefined
		>();
		const [ errorText, setErrorText ] = useState<string | undefined>();

		const {
			data,
			loading: queryLoading,
			error,
		} = useInvoiceEditApplyTemplateModal_GetInvoiceTemplatesWhereQuery( {
			skip: !open,
			variables: {
				orderBy: [
					{
						updatedAt: SortOrderType.desc,
					},
				],
			},
		} );

		useEffect( () => {
			if ( error ) {
				showError( error );
			}
		}, [ error, showError ] );

		const invoiceTemplates = data?.getInvoiceTemplatesWhere?.invoiceTemplates;

		useEffect( () => {
			if ( invoiceTemplate || open ) {
				setSelectedTemplate( invoiceTemplate );
			}
		}, [ invoiceTemplate, open ] );

		useEffect( () => {
			if ( invoiceTemplates ) {
				if ( invoiceTemplates.length === 0 ) {
					setTitle( 'No templates available' );
					setDescription(
						'You haven’t created any templates yet. Once you’ve created a template you can use it to create a new invoice, or apply it to an invoice that hasn’t received any payments yet.'
					);
					setButtonText( 'Done' );
				} else {
					if ( invoiceTemplate ) {
						setDescription(
							<>
								<Typography component='span' className={ styles.description }>
									You previously applied{ ' ' }
									<strong>{ invoiceTemplate.title }</strong> to this invoice.
								</Typography>
								<Typography component='span' className={ styles.description }>
									You can choose a different invoice to apply, or reapply the
									original template.
								</Typography>
							</>
						);
					} else {
						setDescription(
							'Choose an invoice template that you want to apply to this invoice.'
						);
					}
				}
			}
		}, [ invoiceTemplates, invoiceTemplate ] );

		const validationEnabledRef = useRef( false );

		const validate = useCallback(
			( newSelectedTemplate?: boolean ) => {
				if ( validationEnabledRef.current ) {
					if ( !selectedTemplate && !newSelectedTemplate ) {
						setErrorText( 'Select an invoice template to apply' );
						return false;
					} else {
						setErrorText( undefined );
					}
				}
				return true;
			},
			[ selectedTemplate ]
		);

		const handleChange = useCallback(
			( value: string | InvoiceTemplate ) => {
				if ( typeof value === 'string' ) {
					setSelectedTemplate(
						invoiceTemplates?.find(
							( invoiceTemplate ) => invoiceTemplate.title === value
						)
					);
				} else {
					setSelectedTemplate( value );
				}
				validate( true );
			},
			[ invoiceTemplates, validate ]
		);

		const handleClose: OnCloseType = useCallback(
			( event, reason ) => {
				setTitle( 'Apply a template' );
				setDescription( undefined );
				setSelectedTemplate( undefined );
				setButtonText( 'Apply' );
				setErrorText( undefined );
				onClose?.( event, reason );
			},
			[ onClose ]
		);

		const handleClick = useCallback(
			( e: React.MouseEvent ) => {
				if ( !invoiceTemplates || invoiceTemplates.length === 0 ) {
					handleClose( e, 'fromCode' );
				} else {
					validationEnabledRef.current = true;
					if ( validate() && selectedTemplate ) {
						setInvoiceTemplate( selectedTemplate );
						onClose?.( e, 'fromCode' );
					}
				}
			},
			[
				invoiceTemplates,
				handleClose,
				validate,
				selectedTemplate,
				setInvoiceTemplate,
				onClose,
			]
		);

		const loading = queryLoading || !data;

		return (
			<MuiModal
				muiModalProps={ {
					open,
					onClose: handleClose,
					className: styles.modal,
					slotProps: {
						paper: {
							className: styles.modalPaper,
						},
					},
				} }
				title={ title }
				description={ description }
				controls={
					<Button
						loading={ loading }
						onClick={ handleClick }
						data-cy='apply-or-done'
					>
						{ buttonText }
					</Button>
				}
			>
				{ loading ? (
					<Skeleton className={ styles.skeleton } />
				) : !invoiceTemplates?.length ? null : (
					<>
						<Autocomplete<InvoiceTemplate, undefined, true, true>
							className={ styles.input }
							openOnFocus
							blurOnSelect
							autoHighlight
							disableClearable
							options={ invoiceTemplates || [] }
							value={ selectedTemplate || '' }
							getOptionLabel={ ( option ) =>
								typeof option === 'string' ? option : option.title
							}
							isOptionEqualToValue={ ( option, value ) =>
								typeof value === 'string' ? false : option.id === value.id
							}
							renderInput={ ( params ) => (
								<TextField
									{ ...params }
									variant='outlined'
									error={ !!errorText }
									helperText={ errorText }
									label='Invoice Template'
									onBlur={ () => validate( validationEnabledRef.current ) }
									slotProps={ {
										...params.slotProps,
										htmlInput: {
											...params.slotProps.htmlInput,
											'data-cy': 'select-invoice-template',
										},
									} }
								/>
							) }
							onChange={ ( _, value ) => handleChange( value ) }
						/>
						{ selectedTemplate ? (
							<Typography className={ styles.info }>
								Applying this template will overwrite previously entered
								information on the invoice
							</Typography>
						) : null }
					</>
				) }
			</MuiModal>
		);
	};

InvoiceEditApplyTemplateModal.fragments = {
	invoiceEditApplyTemplateModalInvoiceFragment,
};
